<template>
<!-- Main Content -->
    <main class="flex-1 p-6 overflow-y-auto page-container">
      <h1 class="text-2xl font-semibold mb-4">{{ $t('components.order.yourOrders') }}</h1>
      <div class="bg-white p-4 rounded shadow">
        <div class="mb-4 flex items-center gap-2">
          <!-- Filter Popover -->
          <el-form label-position="top" label-width="auto" class="flex flex-row gap-4 p-2 border custom-border">
            <el-tooltip placement="top" :content="$t('components.order.filterOptions')"><i class="fa-solid fa-filter icon"></i></el-tooltip>
            <el-form-item v-if="this.isUserSupplier" :label="$t('components.order.customerFilter')" class="font-bold mb-1">
              <el-select
                v-model="customerFilter"
                :placeholder="$t('components.order.select')"
                @change="setFilter"
              >
                <el-option :label="$t('components.order.all')" :value="undefined"/>
                <el-option
                  v-for="customer in this.customerOptions"
                  :key="customer.value"
                  :label="customer.label"
                  :value="customer.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('components.order.paymentStatus')" class="font-bold mb-1">
              <el-select
                v-model="paymentStatusFilter"
                :placeholder="$t('components.order.select')"
                @change="setFilter"
              >
                <el-option :label="$t('components.order.all')" :value="undefined"/>
                <el-option
                  v-for="payment in this.paymentStatusOptions"
                  :key="payment.value"
                  :label="payment.label"
                  :value="payment.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('components.order.orderStatus')" class="font-bold mb-1 me-2">
              <el-select
                v-model="orderStatusFilter"
                :placeholder="$t('components.order.select')"
                @change="setFilter"
              >
                <el-option :label="$t('components.order.all')" :value="undefined"/>
                <el-option
                  v-for="status in this.orderStatusOptions"
                  :key="status.value"
                  :label="status.label"
                  :value="status.value"
                />
              </el-select>
            </el-form-item>
          </el-form>
        </div>

        <!-- Scrollable Table -->
        <div class="overflow-auto max-h-table mb-4" :key="tableKey">
          <table class="min-w-full table-fixed">
            <thead class="position-sticky top-0 z-10" style="background-color: white;">
              <tr>
                <th class="px-4 py-2">{{ $t('components.order.orderDate') }}</th>
                <th class="px-4 py-2">{{ $t('components.order.orderNumber') }}</th>
                <th v-if="isUserAdmin" class="px-4 py-2">{{ $t('components.order.domain') }}</th>
                <th v-if="isUserAdmin" class="px-4 py-2">{{ $t('components.order.supplierUser') }}</th>
                <th class="px-4 py-2">{{ $t('components.order.customerUser') }}</th>
                <th class="px-4 py-2">{{ $t('components.order.model') }}</th>
                <th class="px-4 py-2">{{ $t('components.order.orderStatus') }}</th>
                <th class="px-4 py-2">{{ $t('components.order.paymentStatus') }}</th>
                <th class="px-4 py-2">{{ $t('components.table.actions') }}</th>
              </tr>
            </thead>
            <tbody v-if="orders.value?.length > 0">
              <tr v-for="order in orders.value" :key="order.orderId" class="max-h-10 overflow-scroll">
                <td class="border px-1 py-2">{{ formatValue(order.orderDate) }}</td>
                <td class="border px-1 py-2">{{ order.orderNumber }}</td>
                <td v-if="isUserAdmin" class="border px-1 py-2">{{ order.domain.urlSuffix }}</td>
                <td v-if="isUserAdmin" class="border px-1 py-2">{{ order.supplier.firstname + ' ' + order.supplier.lastname }}</td>
                <td class="border px-1 py-2">{{ order.customer.firstname + ' ' + order.customer.lastname }}</td>
                <td class="border px-1 py-2">{{ order.model.product.name }}</td>

                 <!-- Editable Order Status -->
                <td class="border px-1 py-2">
                  <template v-if="editingOrderId === order.orderId">
                    <el-select v-model="editedOrder.orderStatus" placeholder="Select status" size="medium">
                      <el-option v-for="status in this.orderStatusOptions"
                        :key="status.value"
                        :label="status.label"
                        :value="status.value"
                      />
                    </el-select>
                  </template>
                  <template v-else>
                    {{ $t(`components.order.${order.orderStatus}`) }}
                  </template>
                </td>

                <!-- Editable Payment Status -->
                <td class="border px-1 py-2">
                  <template v-if="editingOrderId === order.orderId">
                    <el-select v-model="editedOrder.paymentStatus" placeholder="Select status" size="medium">
                      <el-option v-for="payment in this.paymentStatusOptions"
                        :key="payment.value"
                        :label="payment.label"
                        :value="payment.value"
                      />
                    </el-select>
                  </template>
                  <template v-else>
                    {{ $t(`components.order.${order.paymentStatus}`) }}
                  </template>
                </td>

                <!-- Action Buttons -->
                <td class="border px-4 py-2">
                  <div class="action-column">
                    <template v-if="editingOrderId === order.orderId">
                      <el-tooltip :content="$t('components.order.cancel')" placement="left">
                        <el-button @click="cancelEdit" type="warning">
                          <i class="fa-solid fa-xmark" />
                        </el-button>
                      </el-tooltip>
                      <el-tooltip :content="$t('components.order.save')" placement="top">
                        <el-button @click="saveEdit(order as Order)" type="success">
                          <i class="fa-solid fa-check" />
                        </el-button>
                      </el-tooltip>
                    </template>
                    <template v-else>
                      <el-tooltip :content="$t('components.order.details')" placement="left">
                        <el-button @click="openDetailDialog(order as Order)" :disabled="editingOrderId !== null">
                          <i class="fa-solid fa-folder-open" />
                        </el-button>
                      </el-tooltip>
                      <el-tooltip v-if="isUserSupplier" :content="$t('components.order.edit')" placement="top">
                        <el-button @click="startEdit(order as Order)" :disabled="editingOrderId !== null">
                          <i class="fa-solid fa-pen-to-square" />
                        </el-button>
                      </el-tooltip>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else style="position: relative; height: 25rem;">
              <el-empty class="centered" :description="$t('components.order.noOrders')" />
            </tbody>
          </table>
        </div>
      </div>
    </main>

    <el-dialog
      v-model="dialogVisible"
      :title="$t('components.order.orderInfo')"
      :show-close="true"
      style="width: fit-content; max-width: 80rem; min-width: 80rem;"
    >
      <div v-if="selectedOrder" class="scrollable">
        <img :src="selectedOrder.domain.logo" alt="Logo" class="mt-2 mb-4" style="max-height: 3rem;" />

        <!-- Basic Order Info -->

        <div class="row me-3">
          <div class="col-md-6">
            <p><strong>{{ $t('components.order.orderNumber') }}:</strong> {{ selectedOrder.orderNumber }}</p>
            <p><strong>{{ $t('components.order.orderDate') }}:</strong> {{ formatValue(selectedOrder.orderDate) }}</p>
            <p><strong>{{ $t('components.order.deliveryDate') }}:</strong> {{ formatValue(selectedOrder.deliveryDate) }}</p>
            <p><strong>{{ $t('components.order.billingAddress') }}:</strong> {{ selectedOrder.billingAddress }}</p>
            <p><strong>{{ $t('components.order.shippingAddress') }}:</strong> {{ selectedOrder.shippingAddress }}</p>
            <p><strong>{{ $t('components.order.paymentTerms') }}:</strong> {{ selectedOrder.paymentTerms }}</p>
            <p><strong>{{ $t('components.order.additionalInformation') }}:</strong> {{ selectedOrder.additionalInformation }}</p>
          </div>
          <div class="col-md-6">
            <p><strong>{{ $t('components.order.orderStatus') }}:</strong> {{ $t(`components.order.${selectedOrder.orderStatus}`) }}</p>
            <p><strong>{{ $t('components.order.paymentStatus') }}:</strong> {{ $t(`components.order.${selectedOrder.paymentStatus}`) }}</p>
            <p><strong>{{ $t('components.order.quantity') }}:</strong> {{ selectedOrder.quantity }}</p>
            <p><strong>{{ $t('components.order.basePricePerUnit') }}:</strong> {{ selectedOrder.basePricePerUnit }}€</p>
            <p><strong>{{ $t('components.order.totalPrice') }}:</strong> {{ selectedOrder.totalPrice }}€</p>
            <p><strong>{{ $t('components.order.tax') }}:</strong> {{ selectedOrder.tax }}€</p>
            <p><strong>{{ $t('components.order.taxRate') }}:</strong> {{ selectedOrder.taxRate }}%</p>
          </div>
        </div>

        <div class="row me-3">
          <h3 class="font-bold mt-4 mb-4">{{ $t('components.order.userInfo') }}</h3>
          <!-- Supplier Info -->
          <div class="col-md-6">
            <h5 class="font-bold">{{ $t('components.order.supplierUser') }}</h5>
            <p><strong>{{ $t('components.order.username') }}:</strong> {{ selectedOrder.supplier.username }}</p>
            <p><strong>{{ $t('components.order.name') }}:</strong> {{ selectedOrder.supplier.titlePrefix || '' }} {{ selectedOrder.supplier.firstname }} {{ selectedOrder.supplier.lastname }} {{ selectedOrder.supplier.titlePostfix || '' }}</p>
            <p><strong>{{ $t('components.order.email') }}:</strong> {{ selectedOrder.supplier.email }}</p>
          </div>

          <!-- Customer Info -->
          <div class="col-md-6">
            <h5 class="font-bold">{{ $t('components.order.customerUser') }}</h5>
            <p><strong>{{ $t('components.order.username') }}:</strong> {{ selectedOrder.customer.username }}</p>
            <p><strong>{{ $t('components.order.name') }}:</strong> {{ selectedOrder.customer.titlePrefix || '' }} {{ selectedOrder.customer.firstname }} {{ selectedOrder.customer.lastname }} {{ selectedOrder.customer.titlePostfix || '' }}</p>
            <p><strong>{{ $t('components.order.email') }}:</strong> {{ selectedOrder.customer.email }}</p>
          </div>
        </div>

        <!-- Model Info -->
        <h3 class="font-bold mt-4 mb-4">{{ $t('components.order.productInfo') }}</h3>
        <div class="row me-3">
          <div class="col-md-6">
            <p><strong>{{ $t('components.order.productName') }}:</strong> {{ selectedOrder.model.product.name }}</p>
            <p><strong>{{ $t('components.order.bodySide') }}:</strong> {{ selectedOrder.bodySide }}</p>
            <p v-if="selectedOrder.kneeJoint"><strong>{{ $t('components.order.kneeJoint') }}:</strong> {{ selectedOrder.kneeJoint }}</p>
          </div>
          <div class="col-md-6">
            <p><strong>{{ $t('components.order.depth') }}:</strong> {{ selectedOrder.depth }}mm</p>
            <p><strong>{{ $t('components.order.height') }}:</strong> {{ selectedOrder.height }}mm</p>
            <p><strong>{{ $t('components.order.width') }}:</strong> {{ selectedOrder.width }}mm</p>
          </div>
        </div>

        <h5 class="font-bold mt-2 mb-4">{{ $t('components.order.componentInfo') }}</h5>
        <div v-if="selectedOrder.coverOrderItems" class="me-3">
          <div v-for="item in selectedOrder.coverOrderItems" :key="item.orderItemId" class="border custom-border p-2 my-2">
            <p><strong>{{ $t('components.order.componentName') }}:</strong> {{ item.component.name }}</p>
            <p><strong>{{ $t('components.order.color') }}:</strong> {{ item.color }}</p>
            <p v-if="item.engraving"><strong>{{ $t('components.order.engraving') }}:</strong> {{ item.engraving }}</p>
            <p v-if="item.engravingColor"><strong>{{ $t('components.order.engravingColor') }}:</strong> {{ item.engravingColor }}</p>
          </div>
        </div>

        <!-- Image & Scan Files -->
        <div v-if="selectedOrder.imageFiles?.length" class="me-3">
          <h3 class="font-bold mt-4 mb-4">{{ $t('components.order.images') }}</h3>
          <div v-for="img in selectedOrder.imageFiles" :key="img">
            <img :src="img" class="mb-4" style="max-height: 15rem;" :alt="img" />
          </div>
        </div>

        <div v-if="selectedOrder.scanFiles?.length" class="me-3">
          <h3 class="font-bold mt-4 mb-4">{{ $t('components.order.scans') }}</h3>
          <div v-for="scan, id in selectedOrder.scanFiles" :key="scan">
            <a :href="scan" target="_blank">{{ $t('components.order.downloadScan') + ' ' + (id + 1) }}</a>
          </div>
        </div>

        <!-- Data upload -->
        <div class="col-md mb-2 me-3">
          <h3 class="font-bold mt-4 mb-4">{{ $t('components.order-form.dataUploadTitle') }}</h3>
          <OrderFormUploadData
            :order="selectedOrder"
            :fields="uploadDataFields"
            :isExistingOrder="true"
            @progress="handleUploadProgress"
            @editedOrderFiles="saveFileChange"
          />
        </div>
      </div>
    </el-dialog>
</template>

<script lang="ts">
import { Order } from '@/types/api/Business/Order';
import { DomainsWithPaging } from '@/types/api/Raw/domain';
import { UsersWithPaging } from '@/types/api/Raw/user';
import { computed, reactive, ref } from 'vue';
import { Component, Vue } from 'vue-facing-decorator';
import { getOrdersByCustomer, getOrdersBySupplier, updateOrderStatus, updateOrderPaymentStatus, updateOrderFiles } from '@/services/api/orderService';
import { useUserStore } from '@/stores/userStore';
import { OrderStatus, PaymentStatus } from '@/types/enum/order';
import i18n from '@/i18n';
import { Role } from '@/types/enum/user';
import { ElMessage } from 'element-plus';
import OrderForm from '@/components/configurator/OrderForm.vue';
import formUploadDataInitial from '@/data/formUploadDataInitial.json';
import { useUploadStore } from '@/stores/uploadStore';
import OrderFormUploadData from '@/components/forms/OrderFormUploadData.vue';

const OrderStatusSequence: OrderStatus[] = [
  OrderStatus.DRAFT,
  OrderStatus.PENDING,
  OrderStatus.CONFIRMED,
  OrderStatus.PROCESSING,
  OrderStatus.SHIPPED,
  OrderStatus.DELIVERED,
  OrderStatus.CANCELED,
];

@Component({
  methods: { ref },
  components: {
    OrderFormUploadData
  }
})

export default class OrderPage extends Vue {
  userId: number | null = null;
  itemsPerPage = 10;
  userResponse = ref<UsersWithPaging | null>(null);
  orders = ref<Order[]>([]);
  selectedOrder: Order | null = null;
  domainResponse = ref<DomainsWithPaging | null>(null);
  customerFilter: string | undefined = undefined;
  paymentStatusFilter: string | undefined = undefined;
  orderStatusFilter: string | undefined = undefined;
  search = ref('');
  isFetching = ref(false);
  hasNext = ref(false);
  hasPrev = ref(false);
  isEditing = ref(false);
  totalPages = ref(1);
  currentPage = ref(1);
  searchTimeout = 0;
  changeUserDomainModalRef = ref();
  changeRoleModalRef = ref();
  isFetchingData = ref(false);
  userStore = useUserStore();
  uploadStore = useUploadStore();
  isUserSupplier = this.userStore.roles.includes(Role.Supplier);
  isUserAdmin = this.userStore.roles.includes(Role.Supplier) && this.userStore.domain?.urlSuffix === 'aidddo';
  dialogVisible = false;
  seen = new Set<number>();
  options: { label: string; value: string }[] = [];
  editingOrderId: number | null = null;
  editedOrder: Partial<Order> = {};
  tableKey = 0;
  private onRefreshOrders = () => { this.fetchOrders(); };

  async mounted() {
    this.userId = this.userStore.user?.pk || null;
    await this.fetchOrders();
    window.addEventListener('refresh-orders', this.onRefreshOrders);

  }
  beforeUnmount() {
    window.removeEventListener('refresh-orders', this.onRefreshOrders);
  }


  private async fetchOrders() {
    if (!this.userId) return;
    // 1) fetch
    const result = (this.isUserAdmin || this.isUserSupplier)
      ? await getOrdersBySupplier(
        this.userId,
        this.customerFilter,
        this.paymentStatusFilter,
        this.orderStatusFilter
      )
      : await getOrdersByCustomer(
        this.userId,
        this.paymentStatusFilter,
        this.orderStatusFilter
      );

    // 2) force a new reference so Vue notices the change
    // If the service already returns a fresh array, this is harmless.
    this.orders.value = Array.isArray(result) ? [...result] : [];

    // 3) (optional) bump a key to hard-rerender the table if needed
    this.tableKey++;
  }


  async setFilter() {
    await this.fetchOrders();
  }

  state = reactive({
    isLoadingUploadData: false,
  });

  openDetailDialog(order: Order) {
    this.selectedOrder = order;
    this.dialogVisible = true;
  }

  //idk we we need that but since the getUploadDataFields return an error i won't delete it
  addScanFile(event: { target: { files: any; }; }) {
    for (const scan of event.target.files) {
      this.selectedOrder?.scanFiles.push(scan);
    }
  }

  addImageFiles(event: { target: { files: any; }; }) {
    for (const image of event.target.files) {
      this.selectedOrder?.imageFiles.push(image);
    }
  }

  formatValue = (value: unknown): string => {
    if (value == null) return '';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'string' && !isNaN(Date.parse(value))) {
      return new Date(value).toLocaleDateString();
    }
    if (value instanceof Date) return value.toLocaleDateString();
    if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return String(value);
  };

  get uploadDataFields() {
    const addParams = [{ group: 'string', id: 'number' }];

    return formUploadDataInitial.map((row, index) => {
      const methodName = row.action; // Get the function name from the JSON file
      const method = (this as unknown as OrderForm)[methodName as keyof OrderForm]; // Search for functions in the component
      const params = row.params || addParams[index]; // Ambil parameter tambahan dari JSON

      if (typeof method !== 'function') {
        console.error(`Method "${methodName}" not found on the component.`);
        return { ...row, action: undefined }; // Set undefined if the function is not found
      }

      //  Wrap the function with a closure to pass additional parameters
      return {
        ...row,
        action: (event: Event) => method(event, { ...params }),
      };
    });
  }
  
  handleUploadProgress(isUploading: boolean) {
    this.state.isLoadingUploadData = isUploading;
    this.uploadStore.setIsUploading(isUploading);
  }

  get orderStatusOptions() {
    return OrderStatusSequence.map((status, index) => {
      const displayIndex = index + 1;
      const translatedLabel = i18n.global.t(`components.order.${status}`) as string;

      return {
        label: `${displayIndex}. ${translatedLabel}`,
        value: status,
      };
    });
  }

  get paymentStatusOptions() {
    return Object.values(PaymentStatus).map(status => ({
      label: i18n.global.t(`components.order.${status}`) as string,
      value: status
    }))
  }

  get customerOptions() {
    if (!this.orders.value) {
      return this.options
    }
    
    for (const order of this.orders.value) {
      const customer = order.customer
      if (!this.seen.has(customer.userId)) {
        this.seen.add(customer.userId)
        this.options.push({
          label: customer.firstname + ' ' + customer.lastname,
          value: customer.firstname + ' ' + customer.lastname as string
        })
      }
    }

    return this.options
  }

  startEdit(order: Order) {
    this.editingOrderId = order.orderId;
    this.editedOrder = {
      orderStatus: order.orderStatus,
      paymentStatus: order.paymentStatus,
    };
  }

  cancelEdit() {
    this.editingOrderId = null;
    this.editedOrder = {};
  }

  async saveEdit(order: Order) {
    try {
      let updatedOrder: Partial<Order> = {};

      const hasStatusChanged = this.editedOrder.orderStatus !== order.orderStatus;
      const hasPaymentChanged = this.editedOrder.paymentStatus !== order.paymentStatus;

      if (hasStatusChanged) {
          await updateOrderStatus(order.orderId, this.editedOrder.orderStatus!);
          updatedOrder = { ...updatedOrder, orderStatus: this.editedOrder.orderStatus! };
      }

      if (hasPaymentChanged) {
          await updateOrderPaymentStatus(order.orderId);
          updatedOrder = { ...updatedOrder, paymentStatus: this.editedOrder.paymentStatus! };
      }

      if (Object.keys(updatedOrder).length > 0) {
          this.orders.value = this.orders.value.map(o => {
              if (o.orderId === order.orderId) {
                  return { ...o, ...updatedOrder };
              }
              return o;
          });
      }

      ElMessage.success('Order updated successfully');
    } catch (error) {
      console.error('Error updating order:', error);
      ElMessage.error('Failed to update order');
    } finally {
      this.cancelEdit();
    }
  }

  async saveFileChange(editedOrder: Order) {
    let updatedOrder: Partial<Order> = {};

    await updateOrderFiles(this.selectedOrder!.orderId, editedOrder.scanFiles, editedOrder.imageFiles);
    updatedOrder = { ...updatedOrder, scanFiles: editedOrder.scanFiles, imageFiles: editedOrder.imageFiles};

    if (Object.keys(updatedOrder).length > 0) {
      this.orders.value = this.orders.value.map(o => {
          if (o.orderId === editedOrder.orderId) {
              return { ...o, ...updatedOrder };
          }
          return o;
      });
    }
  }
}
</script>

<style scoped>
.scrollable {
  max-height: 60vh;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: auto;
  overflow-x: hidden;
}

.centered {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.custom-border {
  border-radius: 0.5rem;
}

.icon {
  font-size: 1.5rem;
  color: grey;
  padding: 1.3rem 0.5rem 0.5rem 1rem;
  justify-self: center;
}

.el-form-item {
  width: 8rem;
}

.p {
  line-height: 1.4 !important;
}
</style>